export const MAX_CONSOLE_MESSAGE_LENGTH = 4000;
export const MAX_ARRAY_ROWS = 50;
export const MAX_STORED_REQUEST_LOGS = 25;
export const MAX_REPORTED_ERROR_LENGTH = 4000;
export const MAX_SERIALIZED_SIZE = 500_000;

// Transmission budgets — applied before sending to backend
export const MAX_TOTAL_REQUEST_LOGS_CHARS = 150_000;
export const MAX_TOTAL_CONSOLE_LOGS_CHARS = 50_000;
export const MAX_TRANSMISSION_ROWS = 5;
export const MAX_STORED_CONSOLE_LOGS = 100;

function summarizeBinaryLike(value) {
    if (typeof File !== "undefined" && value instanceof File) {
        return {
            type: "File",
            name: value.name,
            size: value.size,
            mimeType: value.type,
        };
    }

    if (typeof Blob !== "undefined" && value instanceof Blob) {
        return {
            type: "Blob",
            size: value.size,
            mimeType: value.type,
        };
    }

    if (value instanceof ArrayBuffer) {
        return {
            type: "ArrayBuffer",
            byteLength: value.byteLength,
        };
    }

    if (ArrayBuffer.isView(value)) {
        return {
            type: value.constructor?.name || "TypedArray",
            byteLength: value.byteLength,
            length: value.length,
        };
    }

    return null;
}

export function sanitizeNetworkValue(value, seen = new WeakSet()) {
    if (value == null) return value;

    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        return value;
    }

    const binarySummary = summarizeBinaryLike(value);
    if (binarySummary) {
        return binarySummary;
    }

    if (value instanceof Error) {
        return {
            name: value.name,
            message: value.message,
            stack: value.stack,
        };
    }

    if (typeof value === "object") {
        if (seen.has(value)) {
            return "[Circular]";
        }
        seen.add(value);
    }

    if (typeof FormData !== "undefined" && value instanceof FormData) {
        const entries = [];
        for (const [key, entryValue] of value.entries()) {
            entries.push({
                key,
                value: sanitizeNetworkValue(entryValue, seen),
            });
        }
        return { type: "FormData", entries };
    }

    if (
        typeof URLSearchParams !== "undefined" &&
        value instanceof URLSearchParams
    ) {
        return {
            type: "URLSearchParams",
            entries: Array.from(value.entries()),
        };
    }

    if (Array.isArray(value)) {
        const total = value.length;
        const kept = value.slice(0, MAX_ARRAY_ROWS);
        const mapped = kept.map((item) => sanitizeNetworkValue(item, seen));
        if (total > MAX_ARRAY_ROWS) {
            mapped.push(`... ${total - MAX_ARRAY_ROWS} more items (${total} total)`);
        }
        return mapped;
    }

    if (typeof value === "object") {
        const sanitizedObject = {};
        for (const [key, entryValue] of Object.entries(value)) {
            sanitizedObject[key] = sanitizeNetworkValue(entryValue, seen);
        }
        return sanitizedObject;
    }

    return String(value);
}

function enforceMaxSize(sanitized) {
    let serialized;
    try {
        serialized = JSON.stringify(sanitized);
    } catch {
        return { __error: "Could not serialize payload" };
    }

    if (serialized.length <= MAX_SERIALIZED_SIZE) {
        return sanitized;
    }

    return {
        __truncated: true,
        __originalSizeChars: serialized.length,
        __maxAllowed: MAX_SERIALIZED_SIZE,
        __preview: serialized.slice(0, MAX_SERIALIZED_SIZE),
    };
}

export function truncateString(
    value,
    maxLength = MAX_CONSOLE_MESSAGE_LENGTH
) {
    if (typeof value !== "string") return value;
    if (value.length <= maxLength) return value;

    return `${value.slice(0, maxLength)}... [truncated ${
        value.length - maxLength
    } chars]`;
}

export function createConsoleLogEntry(
    logType,
    message,
    timestamp = Date.now()
) {
    return {
        type: logType,
        args: [truncateString(message, MAX_CONSOLE_MESSAGE_LENGTH)],
        timestamp,
    };
}

export function createRequestLogEntry(
    responseData,
    timestamp = Date.now()
) {
    const isError = !!responseData?.error;

    const entry = {
        type: isError ? "axios.error" : "axios.success",
        request: sanitizeNetworkValue(responseData?.request),
        response: sanitizeNetworkValue(responseData?.response),
        error: sanitizeNetworkValue(responseData?.error),
        timestamp,
    };

    return enforceMaxSize(entry);
}

export function appendRequestLog(
    previousLogs,
    responseData,
    timestamp = Date.now()
) {
    return [
        ...previousLogs,
        createRequestLogEntry(responseData, timestamp),
    ].slice(-MAX_STORED_REQUEST_LOGS);
}

export function truncateReportedError(errorMessage) {
    if (typeof errorMessage !== "string") {
        return errorMessage;
    }

    return errorMessage.trim().slice(0, MAX_REPORTED_ERROR_LENGTH);
}

/**
 * Append a console log entry to the storage array with in-place dedup.
 *
 * If the new entry is identical to the LAST entry (same type + message),
 * it increments `_occurrences` on the existing entry instead of adding a
 * new slot. This prevents repeated console.log from evicting important
 * errors/warnings/human entries out of the fixed-size buffer.
 *
 * Important (error, warn, human, ai, alert) entries are NEVER collapsed
 * into a preceding normal log — they always get their own slot.
 *
 * Returns the new array (capped at MAX_STORED_CONSOLE_LOGS).
 */
const IMPORTANT_LOG_TYPES = new Set([
    "console.error",
    "console.warn",
    "console.human",
    "console.ai",
    "alert",
]);

export function appendConsoleLog(currentLogs, newEntry) {
    const isImportant = IMPORTANT_LOG_TYPES.has(newEntry.type);
    const last = currentLogs.length > 0
        ? currentLogs[currentLogs.length - 1]
        : null;

    // Collapse if: same type + same message, and the new entry is NOT important
    if (
        !isImportant &&
        last &&
        last.type === newEntry.type &&
        !IMPORTANT_LOG_TYPES.has(last.type)
    ) {
        const lastMsg = Array.isArray(last.args) ? last.args[0] : "";
        const newMsg = Array.isArray(newEntry.args) ? newEntry.args[0] : "";
        if (lastMsg === newMsg) {
            // Mutate-safe: copy array, update last element
            const updated = currentLogs.slice();
            updated[updated.length - 1] = {
                ...last,
                _occurrences: (last._occurrences || 1) + 1,
                _lastSeen: newEntry.timestamp,
                timestamp: newEntry.timestamp, // keep newest timestamp
            };
            return updated;
        }
    }

    // Otherwise append normally, cap at MAX_STORED_CONSOLE_LOGS
    const updated = [...currentLogs, newEntry];
    if (updated.length > MAX_STORED_CONSOLE_LOGS) {
        return updated.slice(-MAX_STORED_CONSOLE_LOGS);
    }
    return updated;
}

// ---------------------------------------------------------------------------
// Lightweight hashing (djb2) — used to dedup logs before transmission.
// Only hashes the identity fields (url, method, status, message), not payloads.
// ---------------------------------------------------------------------------

export function djb2Hash(str) {
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0;
    }
    return hash;
}

export function requestLogKey(entry) {
    const type = entry.type || "";
    const url = entry.request?.url || "";
    const method = entry.request?.method || "";
    const status = String(
        entry.response?.status || entry.error?.status || ""
    );
    return djb2Hash(`${type}|${method}|${url}|${status}`);
}

export function consoleLogKey(entry) {
    const type = entry.type || "";
    const msg = Array.isArray(entry.args)
        ? String(entry.args[0] || "")
        : "";
    return djb2Hash(`${type}|${msg}`);
}

/**
 * Deduplicate an array of log entries by hash key.
 * - Keeps the MOST RECENT occurrence of each unique key (newest data).
 * - Annotates with _occurrences, _firstSeen, _lastSeen so AI knows frequency.
 * - Returns entries in the original order (by first-seen position).
 *
 * @param {Array} entries  – array of { entry, ... } wrappers
 * @param {Function} keyFn – entry → numeric hash
 * @returns {Array} deduplicated wrappers with _occurrences metadata on entry
 */
function deduplicateByKey(entries, keyFn) {
    const map = new Map(); // hash → { wrapper, count, firstTs, lastTs, firstIdx }

    for (let i = 0; i < entries.length; i++) {
        const wrapper = entries[i];
        const key = keyFn(wrapper.entry);
        const ts = wrapper.entry.timestamp || 0;

        if (map.has(key)) {
            const existing = map.get(key);
            existing.count += 1;
            if (ts < existing.firstTs) existing.firstTs = ts;
            if (ts > existing.lastTs) {
                existing.lastTs = ts;
                // Keep newest occurrence's data
                existing.wrapper = wrapper;
            }
        } else {
            map.set(key, {
                wrapper,
                count: 1,
                firstTs: ts,
                lastTs: ts,
                firstIdx: i,
            });
        }
    }

    // Build output sorted by first-seen position (preserves relative order)
    const deduped = Array.from(map.values())
        .sort((a, b) => a.firstIdx - b.firstIdx)
        .map(({ wrapper, count, firstTs, lastTs }) => {
            if (count > 1) {
                return {
                    ...wrapper,
                    entry: {
                        ...wrapper.entry,
                        _occurrences: count,
                        _firstSeen: firstTs,
                        _lastSeen: lastTs,
                    },
                };
            }
            return wrapper;
        });

    return deduped;
}

/**
 * Trim arrays inside a value down to MAX_TRANSMISSION_ROWS for wire efficiency.
 * Keeps structure metadata (keys, types) but reduces row count.
 */
function trimForTransmission(value) {
    if (value == null || typeof value !== "object") return value;

    if (Array.isArray(value)) {
        const total = value.length;
        if (total <= MAX_TRANSMISSION_ROWS) {
            return value.map(trimForTransmission);
        }
        const kept = value
            .slice(0, MAX_TRANSMISSION_ROWS)
            .map(trimForTransmission);
        kept.push(
            `... ${total - MAX_TRANSMISSION_ROWS} more items (${total} total)`
        );
        return kept;
    }

    const out = {};
    for (const [k, v] of Object.entries(value)) {
        out[k] = trimForTransmission(v);
    }
    return out;
}

/**
 * Create a slimmed-down copy of a request log entry for transmission.
 * Preserves type, timestamp, request URL/method, and trimmed data.
 */
function slimRequestEntry(entry) {
    if (!entry || typeof entry !== "object") return entry;
    // Already-truncated entries pass through
    if (entry.__truncated || entry.__error) return entry;

    return {
        type: entry.type,
        timestamp: entry.timestamp,
        request: entry.request
            ? {
                  url: entry.request.url,
                  method: entry.request.method,
                  data: trimForTransmission(entry.request.data),
                  params: trimForTransmission(entry.request.params),
              }
            : undefined,
        response: entry.response
            ? {
                  status: entry.response.status,
                  data: trimForTransmission(entry.response.data),
              }
            : undefined,
        error: entry.error
            ? {
                  message: entry.error.message,
                  status: entry.error.status,
                  statusText: entry.error.statusText,
                  data: trimForTransmission(entry.error.data),
              }
            : undefined,
    };
}

/**
 * FIFO cap for request logs before sending to the backend.
 * - Most recent entries are always kept (newest-first fill).
 * - Errors have priority over success entries.
 * - Returns entries in chronological order (oldest first).
 *
 * Compatible with backend handleReadApplicationLogs which expects
 * { type, request, response, error, timestamp } entries.
 */
export function capRequestLogsForTransmission(
    logs,
    maxChars = MAX_TOTAL_REQUEST_LOGS_CHARS
) {
    if (!Array.isArray(logs) || logs.length === 0) return [];

    // Step 1: Slim all entries
    const slimmed = logs.map((log, i) => ({
        index: i,
        entry: slimRequestEntry(log),
    }));

    // Step 2: Deduplicate — same endpoint+method+status = one entry with count
    const deduped = deduplicateByKey(slimmed, (e) => requestLogKey(e));

    // Step 3: Separate errors from successes for priority
    const errors = [];
    const successes = [];
    for (const wrapper of deduped) {
        if (
            wrapper.entry.type === "axios.error" ||
            wrapper.entry.__truncated ||
            wrapper.entry.__error
        ) {
            errors.push(wrapper);
        } else {
            successes.push(wrapper);
        }
    }

    // Newest first: errors first (newest→oldest), then successes (newest→oldest)
    const prioritized = [
        ...errors.reverse(),
        ...successes.reverse(),
    ];

    // Step 4: Fill budget
    let totalSize = 0;
    const kept = [];
    for (const { entry } of prioritized) {
        let size;
        try {
            size = JSON.stringify(entry).length;
        } catch {
            continue;
        }
        if (totalSize + size > maxChars && kept.length > 0) break;
        totalSize += size;
        kept.push(entry);
    }

    // Restore chronological order
    kept.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
    return kept;
}

/**
 * FIFO cap for console logs before sending to the backend.
 * Keeps most recent entries within the budget.
 * Compatible with backend logs format: { type, args, timestamp }.
 */
export function capConsoleLogsForTransmission(
    logs,
    maxChars = MAX_TOTAL_CONSOLE_LOGS_CHARS
) {
    if (!Array.isArray(logs) || logs.length === 0) return [];

    // Step 1: Wrap entries
    const wrapped = logs.map((log, i) => ({ index: i, entry: log }));

    // Step 2: Deduplicate — same type+message = one entry with count
    const deduped = deduplicateByKey(wrapped, (e) => consoleLogKey(e));

    // Step 3: Separate errors/warnings from info/log for priority
    const important = [];
    const normal = [];
    for (const wrapper of deduped) {
        if (
            wrapper.entry.type === "console.error" ||
            wrapper.entry.type === "console.warn" ||
            wrapper.entry.type === "alert"
        ) {
            important.push(wrapper);
        } else {
            normal.push(wrapper);
        }
    }

    // Newest first: important entries first, then normal
    const prioritized = [
        ...important.reverse(),
        ...normal.reverse(),
    ];

    // Step 4: Fill budget
    let totalSize = 0;
    const kept = [];
    for (const { entry } of prioritized) {
        let size;
        try {
            size = JSON.stringify(entry).length;
        } catch {
            continue;
        }
        if (totalSize + size > maxChars && kept.length > 0) break;
        totalSize += size;
        kept.push(entry);
    }

    // Restore chronological order
    kept.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
    return kept;
}
